import { RequireAccessibleNameWithChildren } from '@prophecy/interfaces/a11y';
import { ArrayElement } from '@prophecy/interfaces/generic';
import React from 'react';
import { LinkProps } from 'react-router-dom';

import { TooltipPropsType } from '../Tooltip';

export enum ButtonCategory {
  default = 'default',
  danger = 'danger'
}

export enum ButtonState {
  hover = 'hover',
  focused = 'focused',
  disabled = 'disabled'
}

export type ButtonCategoryType = `${ButtonCategory}`;

export enum ButtonVariants {
  primary = 'primary',
  secondary = 'secondary',
  secondaryGray = 'secondaryGray',
  tertiary = 'tertiary',
  tertiaryGray = 'tertiaryGray',
  link = 'link',
  linkGray = 'linkGray',
  plain = 'plain'
}

export const ButtonVariantsWithDanger = [
  `${ButtonVariants.primary}`,
  `${ButtonVariants.secondary}`,
  `${ButtonVariants.tertiary}`,
  `${ButtonVariants.link}`
] as const;

export enum IconPlacement {
  left = 'left',
  right = 'right'
}

export enum ButtonSize {
  xs = 'xs',
  s = 's',
  m = 'm',
  l = 'l',
  xl = 'xl',
  xxl = 'xxl'
}

export enum ButtonShape {
  default = 'default',
  circle = 'circle'
}

export enum ButtonAs {
  button = 'button',
  link = 'link',
  anchor = 'anchor'
}

// convert enum to union types so people can pass type="primary" instead of type={ButtonTypes.primary}
export type ButtonVariantsType = `${ButtonVariants}`;
export type ButtonVariantsWithDangerType = ArrayElement<typeof ButtonVariantsWithDanger>;
export type IconPlacementType = `${IconPlacement}`;
export type ButtonSizeType = `${ButtonSize}`;
export type ButtonShapeType = `${ButtonShape}`;
export type ButtonAsType = `${ButtonAs}`;

export type ButtonVariantProps =
  | {
      variant?: ButtonVariantsType;
      danger?: false;
    }
  | {
      variant?: ButtonVariantsWithDangerType;
      danger?: boolean;
    };

export type ButtonBaseProps = RequireAccessibleNameWithChildren &
  Omit<ButtonVariantProps, 'children'> & {
    icon?: React.ReactNode;
    iconPlacement?: IconPlacementType;
    disabled?: boolean;
    block?: boolean;
    loading?: boolean | { delay: number };
    disableWhileLoading?: boolean;
    size?: ButtonSizeType;
    shape?: ButtonShapeType;
    children?: React.ReactNode;
    minWidth?: number;
    tooltip?: TooltipPropsType;
    disablePointerFocus?: boolean;
    as?: React.ElementType;
  } & Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'style'>;

export type ButtonElementType<T extends ButtonAsType> = T extends 'button'
  ? React.ButtonHTMLAttributes<HTMLButtonElement> & ButtonBaseProps
  : T extends 'anchor'
    ? React.AnchorHTMLAttributes<HTMLAnchorElement> & ButtonBaseProps
    : T extends 'link'
      ? LinkProps & ButtonBaseProps
      : never;

export type ButtonRefType<T extends ButtonAsType> = T extends 'button'
  ? HTMLButtonElement
  : T extends 'anchor'
    ? HTMLAnchorElement
    : T extends 'link'
      ? HTMLAnchorElement
      : never;

export type ButtonProps<T extends ButtonAsType = 'button'> = ButtonElementType<T> & {
  elementType?: T;
  'data-test-id': string;
};

export type StyleProps = {
  variant: ButtonVariantsType;
  size: ButtonSizeType;
  hasChildren: boolean;
  loading: boolean;
  shape?: ButtonShapeType;
  disabled?: boolean;
  block?: boolean;
  category: ButtonCategoryType;
  minWidth?: number;
  isGroupItem?: boolean;
  isBadge?: boolean;
  disablePointerFocus?: boolean;
};
